import { createTheme } from '@mui/material/styles';
import { durations, easings, reducedMotionQuery } from './motion';

const lift = `transform ${durations.gentle}ms ${easings.gentle}, box-shadow ${durations.calm}ms ${easings.calm}, border-color ${durations.calm}ms ${easings.calm}`;
const press = `transform ${durations.snappy}ms ${easings.snappy}, box-shadow ${durations.calm}ms ${easings.calm}, background ${durations.calm}ms ${easings.calm}`;

const floraTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#087F6B', light: '#D8F7EE', dark: '#075E52', contrastText: '#FFFFFF' },
    secondary: { main: '#5F9F2F' },
    background: { default: '#F4F7F5', paper: '#FFFFFF' },
    text: { primary: '#17251F', secondary: '#52625B' },
    divider: '#DCE6E1',
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: 'Poppins, Inter, Arial, sans-serif',
    fontSize: 16,
    h4: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.18, letterSpacing: '-0.025em' },
    h5: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.015em' },
    h6: { fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.35 },
    body1: { fontSize: '1.063rem', lineHeight: 1.7 },
    body2: { fontSize: '1rem', lineHeight: 1.6 },
    subtitle1: { fontSize: '1.125rem', fontWeight: 600 },
    subtitle2: { fontSize: '1rem', fontWeight: 600 },
    button: { fontSize: '1rem', fontWeight: 600 },
    caption: { fontSize: '0.875rem', lineHeight: 1.5 },
    overline: { fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.08em', lineHeight: 1.6 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // "Dawn mist": soft mint, peach and sage light drifting slowly behind
        // every page. CSS-only so ordinary pages never pay for WebGL.
        // Every type size in the app is in rem, so raising the root size by 15%
        // lifts all text proportionally while spacing (px) stays unchanged.
        html: { backgroundColor: '#F4F7F5', fontSize: '115%' },
        body: {
          background: 'transparent',
          minHeight: '100vh',
          '&::before': {
            content: '""',
            position: 'fixed',
            inset: '-20vmax',
            zIndex: -1,
            pointerEvents: 'none',
            background: [
              'radial-gradient(38vmax 30vmax at 82% 8%, rgba(198, 240, 222, 0.85), transparent 70%)',
              'radial-gradient(34vmax 26vmax at 12% 18%, rgba(253, 226, 205, 0.55), transparent 70%)',
              'radial-gradient(40vmax 32vmax at 55% 92%, rgba(214, 236, 196, 0.6), transparent 70%)',
              'radial-gradient(30vmax 24vmax at 92% 70%, rgba(204, 232, 240, 0.5), transparent 70%)',
            ].join(', '),
            animation: 'floraDawnMist 48s ease-in-out infinite alternate',
          },
          '&::after': {
            content: '""',
            position: 'fixed',
            inset: 0,
            zIndex: -1,
            pointerEvents: 'none',
            opacity: 0.05,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          },
          [reducedMotionQuery]: { '&::before': { animation: 'none' } },
        },
        '@keyframes floraDawnMist': {
          '0%': { transform: 'translate3d(0, 0, 0) rotate(0deg) scale(1)' },
          '50%': { transform: 'translate3d(-3vmax, 2vmax, 0) rotate(4deg) scale(1.04)' },
          '100%': { transform: 'translate3d(2vmax, -2vmax, 0) rotate(-3deg) scale(1.02)' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #DCE6E1',
          backgroundImage: 'none',
          boxShadow: '0 10px 35px rgba(27, 69, 51, 0.06)',
          transition: lift,
          // Only cards that navigate or open something get the springy lift.
          '&:has(> .MuiCardActionArea-root):hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 18px 44px rgba(27, 69, 51, 0.12)',
          },
          '&:has(> .MuiCardActionArea-root):active': { transform: 'translateY(-1px) scale(0.99)' },
          [reducedMotionQuery]: { transition: 'none', '&:hover, &:active': { transform: 'none !important' } },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', borderRadius: 12, minHeight: 44, padding: '10px 20px', lineHeight: 1.5, alignItems: 'center',
          transition: press,
          '&:active': { transform: 'scale(0.97)' },
          [reducedMotionQuery]: { transition: 'none', '&:active': { transform: 'none' } },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #087F6B, #10A487)',
          boxShadow: '0 8px 20px rgba(8, 127, 107, 0.2)',
          '&:hover': {
            background: 'linear-gradient(135deg, #075E52, #087F6B)',
            boxShadow: '0 10px 24px rgba(8, 127, 107, 0.28)',
          },
        },
        outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontSize: '1rem',
            backgroundColor: '#FFFFFF',
            '& fieldset': { borderWidth: 2, borderColor: '#B8C8C0' },
            '&:hover fieldset': { borderColor: '#087F6B' },
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            '&.MuiInputLabel-shrink': {
              zIndex: 1,
              marginLeft: '-5px',
              paddingLeft: '5px',
              paddingRight: '5px',
              backgroundColor: '#FFFFFF',
            },
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#EFF6F2', fontWeight: 700, fontSize: '0.938rem',
            color: '#075E52', borderBottom: '2px solid #CFE1D8', padding: '14px 16px',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: { '& .MuiTableCell-body': { fontSize: '0.938rem', borderBottom: '1px solid #E4ECE8', padding: '14px 16px' } },
      },
    },
    MuiTableContainer: { styleOverrides: { root: { border: '1px solid #DCE6E1', borderRadius: 14 } } },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, minHeight: 32 },
        clickable: { transition: press, '&:active': { transform: 'scale(0.96)' }, [reducedMotionQuery]: { transition: 'none' } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { transition: press, '&:active': { transform: 'scale(0.92)' }, [reducedMotionQuery]: { transition: 'none' } },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: '#DCE6E1' } } },
    MuiAlert: { styleOverrides: { root: { fontSize: '0.938rem', fontWeight: 500, borderWidth: 1 } } },
    MuiAppBar: { styleOverrides: { root: { borderBottom: '1px solid #DCE6E1', boxShadow: '0 4px 18px rgba(27, 69, 51, 0.08)' } } },
  },
});

export default floraTheme;
